/**
 * offlineSyncLogic.js
 * Pure, testable functions for PWA offline log save/sync logic.
 * Extracted from useShopDashboard.js to enable unit testing without mocking Supabase/IDB.
 */

/**
 * Build an offline payload for IndexedDB storage.
 * Captures userId and shopId at save time so they survive logout/shop-switch.
 */
export function buildOfflinePayload(logData, userId, shopId) {
    return {
        ...logData,
        id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        isOffline: true,
        userId: userId || null,
        shopId: shopId || null,
    };
}

/**
 * Determine if a stored offline log should be synced by the current user.
 * - Returns true if the log belongs to the current user.
 * - Returns true if the log has no userId (legacy entries before this fix).
 * - Returns false if the log belongs to a different user.
 */
export function shouldSyncLog(log, currentUserId) {
    if (!currentUserId) return false;
    const logOwnerId = log.userId || currentUserId; // Legacy fallback
    return logOwnerId === currentUserId;
}

/**
 * Transform stored offline logs into the raw log shape expected by useFeedAggregator.
 * This allows the feed to display queued offline logs without hitting Supabase.
 * 
 * @param {Array} pendingLogs - Logs from offlineStorage.getPendingLogs()
 * @param {Object} activeUser - The current user object { id, name, avatar }
 * @returns {Array} Raw log objects compatible with aggregateLogs()
 */
export function buildOfflineFeedItems(pendingLogs, activeUser) {
    if (!pendingLogs || pendingLogs.length === 0) return [];

    return pendingLogs.map(p => ({
        id: p.id,
        user_id: p.userId || activeUser?.id || null,
        shop_id: p.shopId || null,
        site_id: p.siteId || null,
        created_at: p.timestamp || new Date().toISOString(),
        content: p.posts?.[0]?.content || '',
        images: p.posts?.[0]?.images || [],
        conditions: p.conditions || {},
        sightings: p.sightings || [],
        privacy: 'public',
        isOffline: true,
        profiles: {
            name: activeUser?.name || 'Me',
            avatar: activeUser?.avatar || ''
        },
        sites: null,  // Will be resolved by aggregator from coreState.diveSites
        shops: null
    }));
}

/**
 * Build a Supabase insert payload from a stored offline log.
 * Uses the STORED userId/shopId, not the current session state.
 * Returns null if the log shouldn't be synced by this user.
 */
export function buildSyncPayload(log, currentUserId, resolvedImages = []) {
    if (!shouldSyncLog(log, currentUserId)) return null;

    const logOwnerId = log.userId || currentUserId;
    const post = log.posts?.[0] || {};

    return {
        user_id: logOwnerId,
        site_id: log.siteId,
        shop_id: log.shopId,
        created_at: log.timestamp === 'Just now' ? new Date() : (log.timestamp || new Date()),
        content: post.content || '',
        images: resolvedImages,
        conditions: log.conditions || {},
        sightings: log.sightings || [],
        privacy: 'public'
    };
}
